/**
 * Platform Admin Dashboard
 */

import React, { useEffect, useState } from 'react';
import { platformClient, PlatformStats } from '../api/platformClient';
import { Building2, Users, MessageSquare, Clock } from 'lucide-react';

export default function DashboardPage() {
  const [stats, setStats] = useState<PlatformStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const data = await platformClient.getStats();
        setStats(data);
      } catch (err: any) {
        setError(err.response?.data?.error?.message || 'Failed to load statistics');
      } finally {
        setIsLoading(false);
      }
    };

    fetchStats();
  }, []);

  if (isLoading) {
    return <div style={styles.loading}>Loading...</div>;
  }

  if (error) {
    return <div style={styles.error}>{error}</div>;
  }

  return (
    <div style={styles.container}>
      <h1 style={styles.title}>Platform Dashboard</h1>

      <div style={styles.statsGrid}>
        <StatCard
          icon={<Building2 size={24} />}
          label="Organizations"
          value={stats?.organizations.total || 0}
          color="#6366f1"
        />
        <StatCard
          icon={<Users size={24} />}
          label="Active Users"
          value={stats?.users.active || 0}
          subValue={`/ ${stats?.users.total || 0} total`}
          color="#22c55e"
        />
        <StatCard
          icon={<MessageSquare size={24} />}
          label="Active Rooms"
          value={stats?.rooms.active || 0}
          subValue={`/ ${stats?.rooms.total || 0} total`}
          color="#f59e0b"
        />
        <StatCard
          icon={<Clock size={24} />}
          label="Minutes Used"
          value={stats?.usage.totalMinutes || 0}
          color="#ec4899"
        />
      </div>

      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>Organizations by Plan</h2>
        <div style={styles.planGrid}>
          {Object.entries(stats?.organizations.byPlan || {}).map(([plan, count]) => (
            <div key={plan} style={styles.planCard}>
              <span style={styles.planName}>{plan.toUpperCase()}</span>
              <span style={styles.planCount}>{count}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

interface StatCardProps {
  icon: React.ReactNode;
  label: string;
  value: number;
  subValue?: string;
  color: string;
}

function StatCard({ icon, label, value, subValue, color }: StatCardProps) {
  return (
    <div style={styles.statCard}>
      <div style={{ ...styles.iconWrapper, backgroundColor: `${color}20`, color }}>
        {icon}
      </div>
      <div style={styles.statContent}>
        <span style={styles.statLabel}>{label}</span>
        <span style={styles.statValue}>
          {value.toLocaleString()}
          {subValue && <span style={styles.statSubValue}>{subValue}</span>}
        </span>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: '2rem',
  },
  title: {
    color: '#f8fafc',
    fontSize: '1.5rem',
    fontWeight: 700,
    marginBottom: '2rem',
  },
  loading: {
    color: '#94a3b8',
    padding: '2rem',
    textAlign: 'center',
  },
  error: {
    color: '#fecaca',
    backgroundColor: '#7f1d1d',
    padding: '1rem',
    borderRadius: '8px',
    margin: '2rem',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
    gap: '1.5rem',
    marginBottom: '2rem',
  },
  statCard: {
    backgroundColor: '#1e293b',
    borderRadius: '12px',
    padding: '1.5rem',
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
  },
  iconWrapper: {
    width: '48px',
    height: '48px',
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  statContent: {
    display: 'flex',
    flexDirection: 'column',
  },
  statLabel: {
    color: '#94a3b8',
    fontSize: '0.875rem',
  },
  statValue: {
    color: '#f8fafc',
    fontSize: '1.5rem',
    fontWeight: 700,
  },
  statSubValue: {
    color: '#64748b',
    fontSize: '0.875rem',
    fontWeight: 400,
    marginLeft: '0.25rem',
  },
  section: {
    backgroundColor: '#1e293b',
    borderRadius: '12px',
    padding: '1.5rem',
  },
  sectionTitle: {
    color: '#f8fafc',
    fontSize: '1rem',
    fontWeight: 600,
    marginBottom: '1rem',
  },
  planGrid: {
    display: 'flex',
    gap: '1rem',
    flexWrap: 'wrap',
  },
  planCard: {
    backgroundColor: '#334155',
    borderRadius: '8px',
    padding: '1rem 1.5rem',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    minWidth: '100px',
  },
  planName: {
    color: '#94a3b8',
    fontSize: '0.75rem',
    fontWeight: 500,
  },
  planCount: {
    color: '#f8fafc',
    fontSize: '1.5rem',
    fontWeight: 700,
  },
};
